import { useCallback, useEffect, useRef, useState } from "react";

function clampPosition(x, y, width = 360, height = 120) {
  if (typeof window === "undefined") return { x, y };
  const maxX = Math.max(8, window.innerWidth - width - 8);
  const maxY = Math.max(8, window.innerHeight - height - 8);
  return {
    x: Math.min(Math.max(8, x), maxX),
    y: Math.min(Math.max(8, y), maxY),
  };
}

/**
 * Painel flutuante arrastável com posição persistida em sessionStorage.
 */
export default function useDraggablePanel(storageKey, defaultPos) {
  const [position, setPosition] = useState(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Number.isFinite(parsed?.x) && Number.isFinite(parsed?.y)) {
          return { x: parsed.x, y: parsed.y };
        }
      }
    } catch (_) {
      /* ignore */
    }
    return typeof defaultPos === "function" ? defaultPos() : defaultPos;
  });
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const panelRef = useRef(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(position));
    } catch (_) {
      /* ignore */
    }
  }, [position, storageKey]);

  const beginDrag = useCallback((clientX, clientY, target) => {
    if (target instanceof HTMLElement && target.closest("button, a, input, textarea")) {
      return false;
    }
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return false;
    dragOffsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    setDragging(true);
    return true;
  }, []);

  const onDragHandleMouseDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      if (beginDrag(e.clientX, e.clientY, e.target)) {
        e.preventDefault();
      }
    },
    [beginDrag]
  );

  const onDragHandleTouchStart = useCallback(
    (e) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      if (beginDrag(touch.clientX, touch.clientY, e.target)) {
        e.preventDefault();
      }
    },
    [beginDrag]
  );

  useEffect(() => {
    if (!dragging) return undefined;
    const moveTo = (clientX, clientY) => {
      const rect = panelRef.current?.getBoundingClientRect();
      setPosition(
        clampPosition(
          clientX - dragOffsetRef.current.x,
          clientY - dragOffsetRef.current.y,
          rect?.width || 360,
          rect?.height || 120
        )
      );
    };
    const onMove = (e) => {
      e.preventDefault();
      moveTo(e.clientX, e.clientY);
    };
    const onTouchMove = (e) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      e.preventDefault();
      moveTo(touch.clientX, touch.clientY);
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onUp);
    window.addEventListener("touchcancel", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onUp);
    };
  }, [dragging]);

  return {
    position,
    dragging,
    panelRef,
    onDragHandleMouseDown,
    onDragHandleTouchStart,
  };
}
